import React                             from 'react';
import SaveIndicator                     from '@/TokamapEditor/ux/SaveIndicator';
import leftSidebarStore                  from '@/TokamapEditor/stores/leftSidebarStore';
import { observer }                      from 'mobx-react-lite';
import { FormatPainterOutlined }         from '@ant-design/icons';
import { FontColorsOutlined }            from '@ant-design/icons';
import View                              from '@widesk-ui/components/View';
import Alert                             from '@widesk-ui/components/Alert';
import { message }                       from '@widesk-ui/hooks/useMessage';
import Field                             from '@/TokamapEditor/ux/AreaSidebar/Field';
import ColorButton                       from '@/TokamapEditor/ux/AreaSidebar/ColorButton';
import EditableText                      from '@widesk-ui/components/form/EditableText';
import SliderInputNumberFeature          from '@/TokamapEditor/ux/AreaSidebar/SliderInputNumberFeature';
import SliderInputNumber                 from '@/TokamapEditor/ux/AreaSidebar/SliderInputNumber';
import { MAX_HEIGHT_METERS }             from '@/TokamapEditor/ux/AreaSidebar/ThreeDOptions';
import { SLIDER_MAX_HEIGHT_METERS }      from '@/TokamapEditor/ux/AreaSidebar/ThreeDOptions';
import SwitchField                       from '@/TokamapEditor/ux/SwitchField';
import SidebarTabs                       from '@/TokamapEditor/ux/AreaSidebar/SidebarTabs';
import NormalHoverToggle                 from '@/TokamapEditor/ux/AreaSidebar/NormalHoverToggle';
import { NormalHoverScope }              from '@/TokamapEditor/ux/AreaSidebar/NormalHoverToggle';
import ThemePlaceTypesField              from '@/TokamapEditor/ux/ThemePlaceTypesField';
import themeStore                        from '@/TokamapEditor/stores/themeStore';
import { Theme }                         from '@/TokamapEditor/stores/themeStore';
import useDesignTokens                   from '@/hooks/useDesignTokens';
import Select                            from '@widesk-ui/components/form/Select';
import { zoomAppearanceOptions }         from '@/TokamapEditor/helpers/zoomAppearance';
import { DEFAULT_TEXT_FIXED_SIZE_PX }    from '@/TokamapEditor/ux/constants';
import { SLIDER_MAX_TEXT_FIXED_SIZE_PX } from '@/TokamapEditor/ux/constants';
import { MAX_TEXT_FIXED_SIZE_PX }        from '@/TokamapEditor/ux/constants';

type Section = 'appearance' | 'text';

/** Même valeur que `DEFAULT_BORDER_WIDTH` de `MapLibreAdapter` : ce que rend une bordure dont
 * l'épaisseur n'est pas renseignée. */
const DEFAULT_BORDER_WIDTH = 1;

const SCOPE_NOTES: Record<NormalHoverScope, string> = {
	normal: `Les réglages ci-dessous s'appliquent à l'état normal.`,
	hover: `Les réglages ci-dessous s'appliquent quand le visiteur survole la forme.`,
};

/**
 * Contenu de la sous-sidebar d'un thème, en deux temps :
 *  - thème neuf (`themeId` absent) : on ne demande que le nom. Le valider enregistre, puis la
 *    sidebar bascule en modification sur le thème créé — sans fermeture/réouverture.
 *  - modification : nom + onglets d'apparence. Chaque changement enregistre.
 *
 * Les champs d'apparence réutilisent ceux de la sidebar d'une forme (`ColorButton`,
 * `NormalHoverToggle`, `Field`) pour que les deux écrans restent cohérents.
 *
 * Ce composant pose lui-même son padding (`openThemeForm` passe `padding: [0, 0]`) : les onglets
 * doivent aller d'un bord à l'autre, leur trait de séparation en dépend — même disposition que
 * `AreaSidebar`.
 */
/**
 * Nom du thème, modifiable en place dans le TITRE du tiroir (voir `openThemeForm`) plutôt que dans un
 * champ du corps : c'est déjà le titre affiché, un second endroit pour le même nom serait redondant.
 *
 * Lit le thème vivant dans le store, donc le titre suit le renommage sans que l'appelant ait à
 * repousser une nouvelle valeur dans les options du tiroir.
 */
export const ThemeTitle = observer(({ themeId }: { themeId: string; }) => {
	const theme = themeStore.getTheme(themeId);

	if (!theme) return null;

	return (
		<EditableText
			value={theme.name}
			placeholder="Nom du thème"
			errorMessage="Impossible de renommer le thème"
			onSubmit={async name => {
				// `EditableText` affiche le message de l'`Error` levée, sous le champ resté ouvert.
				if (!name) throw new Error('Le nom est obligatoire.');

				await themeStore.renameTheme(theme.id, name);
			}}
		/>
	);
});

const ThemeForm = observer((props: { themeId: string; }) => {
	// Relu dans le store à chaque rendu (et non reçu en prop) pour que le renommage s'y reflète.
	const theme = themeStore.getTheme(props.themeId);

	// Même indicateur que la fiche d'un type : cet écran n'a pas non plus de bouton d'enregistrement,
	// il faut donc que chaque modification dise qu'elle est partie. L'état vient du store, seul à
	// savoir quand une écriture commence et se termine (voir `themeStore._commit`).
	React.useEffect(() => {
		leftSidebarStore.setLowerOption('renderTopRight', () => <SaveIndicator state={themeStore.saveState} />);
	}, [themeStore.saveState]);

	// Thème supprimé depuis une autre sidebar/session pendant que ce formulaire était ouvert.
	if (!theme) {
		return (
			<View paddingV="sm" paddingH="md">
				<Alert type="warning" message="Ce thème n'existe plus." />
			</View>
		);
	}

	return <ThemeEditor theme={theme} />;
});

const ThemeEditor = observer(({ theme }: { theme: Theme; }) => {
	const [section, setSection] = React.useState<Section>('appearance');
	const [scope, setScope] = React.useState<NormalHoverScope>('normal');
	const tokens = useDesignTokens();

	const style = theme.appearance[scope];
	const textStyle = theme.text[scope];

	// Une modification part en requête immédiatement (le store revient à l'état précédent si elle
	// échoue) : c'est ici qu'on présente l'échec.
	// La liste des thèmes se réaligne d'elle-même sur le store (voir l'effet de `ThemeListSidebar`) :
	// rien à notifier ici, seule l'erreur est à présenter.
	const save = async (task: Promise<unknown>) => {
		try {
			await task;
		} catch (err) {
			message.exception(err);
		}
	};

	return (
		<View>
			<View p="md" style={{ borderBottom: `1px solid ${tokens.border.subtle}` }}>
				<ThemePlaceTypesField theme={theme} />
			</View>

			<SidebarTabs
				value={section}
				onChange={setSection}
				options={[
					{ value: 'appearance', label: 'Apparence', icon: <FormatPainterOutlined /> },
					{ value: 'text', label: 'Texte', icon: <FontColorsOutlined /> },
				]}
			/>

			<View p="md">
				{section === 'text' ? (
					<View gap="md">
						<ScopeToggle value={scope} onChange={setScope} />

						<Alert message="Le contenu du texte reste propre à chaque forme. Sans taille fixe dans le thème, chaque forme garde aussi sa propre taille." />

						<SwitchField
							label="Taille fixe"
							help={scope === 'hover'
								? "impose au survol une taille à l'écran différente de celle de l'état normal."
								: "impose aux formes liées une taille à l'écran, quel que soit le zoom, quitte à dépasser de la forme."}
							value={!!textStyle.fixedSizePx}
							onChange={checked => save(
								themeStore.setThemeText(theme.id, scope, { fixedSizePx: checked ? DEFAULT_TEXT_FIXED_SIZE_PX : null }),
							)}
						/>

						{!!textStyle.fixedSizePx && (
							<Field label="Taille (px à l'écran)">
								<ThemeFixedSizeField
									value={textStyle.fixedSizePx}
									onSave={px => save(
										themeStore.setThemeText(theme.id, scope, { fixedSizePx: px }),
									)}
								/>
							</Field>
						)}

						<Field label="Couleurs">
							<View row gap={6} wrap>
								<ColorButton
									label="Texte"
									value={textStyle.color}
									onChange={color => save(
										themeStore.setThemeText(theme.id, scope, { color }),
									)}
								/>

								<ColorButton
									label="Contour"
									value={textStyle.bgColor}
									onChange={color => save(
										themeStore.setThemeText(theme.id, scope, { bgColor: color }),
									)}
								/>
							</View>
						</Field>
					</View>
				) : (
					<View gap="md">
						<ScopeToggle value={scope} onChange={setScope} />

						<Field label="Couleurs">
							<View row gap={6} wrap>
								<ColorButton
									label="Fond"
									value={style.bgColor}
									onChange={color => save(
										themeStore.setThemeStyle(theme.id, scope, { bgColor: color }),
									)}
								/>

								<ColorButton
									label="Bordure"
									value={style.borderColor}
									onChange={color => save(
										themeStore.setThemeStyle(theme.id, scope, { borderColor: color }),
									)}
								/>
							</View>
						</Field>

						<Field label="Épaisseur de bordure">
							<ThemeBorderWidthField
								value={style.borderWidth ?? DEFAULT_BORDER_WIDTH}
								onSave={width => save(
									themeStore.setThemeStyle(theme.id, scope, { borderWidth: width }),
								)}
							/>
						</Field>

						<SwitchField
							label="Bordure en pointillés"
							help="affiche la bordure en trait pointillé plutôt qu'en trait plein."
							value={!!style.borderDashed}
							onChange={checked => save(
								themeStore.setThemeStyle(theme.id, scope, { borderDashed: checked }),
							)}
						/>

						<View h={1} bg={tokens.border.default} />

						{scope === 'hover' ? (
							<>
								<Field
									label="Hauteur"
									help="Hauteur des formes liées au survol, visible quand la carte est inclinée. À zéro, celle de l'état normal."
								>
									<ThemeMetersField
										value={theme.hoverHeight ?? 0}
										onSave={height => save(themeStore.setThemeHoverHeights(theme.id, { hoverHeight: height > 0 ? height : undefined }))}
									/>
								</Field>

								<Field
									label="Hauteur au-dessus du sol"
									help="Décolle le volume des formes liées au survol. À zéro, comme à l'état normal."
								>
									<ThemeMetersField
										value={theme.hoverElevation ?? 0}
										onSave={elevation => save(themeStore.setThemeHoverHeights(theme.id, { hoverElevation: elevation > 0 ? elevation : undefined }))}
									/>
								</Field>
							</>
						) : (
							<>
								<Field
									label="Hauteur"
									help="Visible quand la carte est inclinée. À zéro, chaque forme garde sa propre hauteur."
								>
									<ThemeMetersField
										value={theme.height ?? 0}
										onSave={height => save(themeStore.setThemeHeight(theme.id, height > 0 ? height : undefined))}
									/>
								</Field>

								<Field
									label="Hauteur au-dessus du sol"
									help="Décolle le volume des formes liées quand la carte est inclinée. À zéro, chaque forme garde la sienne."
								>
									<ThemeMetersField
										value={theme.elevation ?? 0}
										onSave={elevation => save(themeStore.setThemeElevation(theme.id, elevation > 0 ? elevation : undefined))}
									/>
								</Field>
							</>
						)}

						<View h={1} bg={tokens.border.default} />

						<Field
							label="Apparition au zoom"
							help="Les formes liées n'apparaissent qu'une fois la carte zoomée d'autant depuis sa vue de départ, dans l'aperçu et la carte publiée. Dans l'éditeur, elles restent affichées, estompées tant que ce zoom n'est pas atteint. « Dès la vue de départ » laisse chaque forme garder son propre réglage."
						>
							<Select
								value={theme.minZoomOffset ?? ''}
								options={zoomAppearanceOptions(theme.minZoomOffset)}
								onChange={(value: unknown) => save(
									themeStore.setThemeMinZoomOffset(theme.id, typeof value === 'number' ? value : undefined),
								)}
							/>
						</Field>
					</View>
				)}
			</View>
		</View>
	);
});

/** Bascule Normal / Au survol, commune aux deux onglets, avec la note qui va avec. */
const ScopeToggle = ({ value, onChange }: {
	value: NormalHoverScope;
	onChange: (value: NormalHoverScope) => void;
}) => {
	const tokens = useDesignTokens();

	return (
		<View gap={6}>
			<NormalHoverToggle value={value} onChange={onChange} />

			<View color={tokens.text.muted} size={11.5} style={{ fontStyle: 'italic' }}>
				{SCOPE_NOTES[value]}
			</View>
		</View>
	);
};

/**
 * Épaisseur de bordure du thème. La valeur suit le glissé localement (`dragging`) et n'est
 * enregistrée qu'au relâchement : sans ça, chaque frame du curseur partirait en requête.
 *
 * Après l'enregistrement on relâche l'état local — `themeStore` applique la valeur de façon
 * optimiste, donc `value` est déjà à jour, et un échec (retour arrière du store) réaffiche
 * l'ancienne valeur au lieu de rester bloqué sur celle qu'on a tentée.
 */
const ThemeBorderWidthField = ({ value, onSave }: {
	value: number;
	onSave: (width: number) => Promise<void>;
}) => {
	const [dragging, setDragging] = React.useState<number>();

	return (
		<SliderInputNumberFeature
			value={dragging ?? value}
			loading={false}
			inputProps={{ min: 1, max: 20 }}
			onChange={setDragging}
			onAfterChange={async width => {
				setDragging(width);
				await onSave(width);
				setDragging(undefined);
			}}
		/>
	);
};

const ThemeFixedSizeField = ({ value, onSave }: {
	value: number;
	onSave: (px: number) => Promise<void>;
}) => {
	const [dragging, setDragging] = React.useState<number>();

	return (
		<SliderInputNumber
			value={dragging ?? value}
			min={1}
			max={SLIDER_MAX_TEXT_FIXED_SIZE_PX}
			inputMax={MAX_TEXT_FIXED_SIZE_PX}
			step={1}
			suffix="px"
			onChange={v => setDragging(Number(v) || 1)}
			onAfterChange={async v => {
				const px = Math.max(1, Number(v) || 1);

				setDragging(px);
				await onSave(px);
				setDragging(undefined);
			}}
		/>
	);
};

/** Même principe que `ThemeBorderWidthField` : enregistrée au relâchement seulement. */
const ThemeMetersField = ({ value, onSave }: {
	value: number;
	onSave: (meters: number) => Promise<void>;
}) => {
	const [dragging, setDragging] = React.useState<number>();

	return (
		<SliderInputNumber
			value={dragging ?? value}
			min={0}
			max={SLIDER_MAX_HEIGHT_METERS}
			inputMax={MAX_HEIGHT_METERS}
			step={1}
			suffix="m"
			onChange={v => setDragging(Number(v) || 0)}
			onAfterChange={async v => {
				const meters = Number(v) || 0;

				setDragging(meters);
				await onSave(meters);
				setDragging(undefined);
			}}
		/>
	);
};

export default ThemeForm;
